import { useId, useState } from "react";
import { ArrowRight, Check, Crown, Zap } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { usePublicPlans } from "../hooks/usePublicPlans";
import { planPricing } from "../lib/plan-pricing";
import type { PublicPlan } from "../lib/api";
import { useI18n } from "../i18n";

type ShopKind = "membership" | "topup";

export function PricingPage() {
  const { plans, online } = usePublicPlans();
  const { t } = useI18n();
  const navigate = useNavigate();
  const id = useId();
  const [kind, setKind] = useState<ShopKind>("membership");
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const candidates = plans.filter((plan) => plan.productKind === kind);
  const selected = candidates.find((plan) => plan.id === selectedId)
    ?? candidates.find((plan) => plan.recommended)
    ?? candidates[0];
  const tabs: Array<{ value: ShopKind; label: string; icon: typeof Crown }> = [
    { value: "membership", label: "会员充值", icon: Crown },
    { value: "topup", label: "流量包加速", icon: Zap },
  ];

  const openAccount = (plan: PublicPlan) => {
    navigate(`/account/plans?plan=${encodeURIComponent(plan.id)}`);
  };

  return (
    <>
      <section className="page-hero compact-hero">
        <div className="container centered narrow">
          <h1>{t("选择适合你的会员权益")}</h1>
          <p>{t("会员充值与高速流量包分开选择，价格和有效期清晰展示。")}</p>
        </div>
      </section>

      <section className="section pricing-section">
        <div className="container pricing-shop">
          <div className="member-tabs" role="tablist" aria-label={t("会员分类")}>
            {tabs.map(({ value, label, icon: Icon }) => (
              <button
                key={value}
                type="button"
                id={`${id}-tab-${value}`}
                role="tab"
                aria-controls={`${id}-panel`}
                aria-selected={kind === value}
                tabIndex={kind === value ? 0 : -1}
                className={kind === value ? "is-selected" : ""}
                onClick={() => {
                  setKind(value);
                  setSelectedId(null);
                }}
              >
                <Icon aria-hidden="true" />{t(label)}
              </button>
            ))}
          </div>

          <div id={`${id}-panel`} role="tabpanel" aria-labelledby={`${id}-tab-${kind}`}>
            <div className="pricing-shop-heading">
              <div>
                <h2>{t(kind === "membership" ? "选择会员" : "选择流量包")}</h2>
                <p>{t(kind === "membership" ? "会员有效期以所选套餐为准，包含高速流量与多设备加速。" : "加购流量随当前会员周期到期，不会延长会员时长。")}</p>
              </div>
              <span>{t(online ? "价格实时同步" : "价格以会员中心为准")}</span>
            </div>

            {candidates.length > 0 ? (
              <div className="member-card-grid pricing-member-grid" role="radiogroup" aria-label={t("选择会员或流量包")}>
                {candidates.map((plan) => {
                  const isSelected = selected?.id === plan.id;
                  const price = planPricing(plan);
                  return (
                    <label key={plan.id} className={`member-plan-card${isSelected ? " is-selected" : ""}`}>
                      <input
                        type="radio"
                        name={`${id}-plan`}
                        checked={isSelected}
                        onChange={() => setSelectedId(plan.id)}
                      />
                      <span className="member-card-top">
                        <span>{t(plan.name)}</span>
                        <span className="member-card-check">{isSelected && <Check aria-hidden="true" />}</span>
                      </span>
                      {plan.recommended && <span className="member-recommended">{t("推荐")}</span>}
                      <span className="member-card-price"><small>¥</small>{plan.priceCny.toFixed(2)}</span>
                      {price.original != null && (
                        <span className="member-discount">
                          <del>¥{(price.original / 100).toFixed(2)}</del>
                          <span>{t("省 {percent}%", { percent: price.percent ?? 0 })}</span>
                        </span>
                      )}
                      <span className="member-card-period">
                        {t(kind === "membership" ? "{days} 天有效" : "随会员周期到期", { days: plan.validityDays })}
                      </span>
                      <span className="member-card-quota">
                        <strong>{plan.trafficGb} GB</strong>
                        <small>{t(kind === "membership" ? "每30天高速流量" : "高速流量")}</small>
                      </span>
                      <span className="member-card-description">{t(plan.description)}</span>
                    </label>
                  );
                })}
              </div>
            ) : (
              <div className="member-empty">
                <Zap aria-hidden="true" />
                <span>{t(kind === "membership" ? "暂无可购买会员，请稍后查看。" : "暂无可购买流量包，请稍后查看。")}</span>
              </div>
            )}

            {selected && (
              <div className="pricing-shop-action">
                <p>
                  <span>{t("已选择")}</span>
                  <strong>{t(selected.name)}</strong>
                  <b>¥{selected.priceCny.toFixed(2)}</b>
                </p>
                <button type="button" className="button" onClick={() => openAccount(selected)}>
                  {t(kind === "membership" ? "前往会员中心开通" : "前往会员中心加购")}
                  <ArrowRight aria-hidden="true" />
                </button>
              </div>
            )}
          </div>

          <p className="pricing-note">{t(online ? "套餐和价格已与线上系统实时同步。" : "正在使用缓存套餐信息，最终以账户中心为准。")}</p>
        </div>
      </section>
    </>
  );
}
